
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import {
  Users, Briefcase, CreditCard, DollarSign, Calendar,
  CheckCircle, Clock, Trash2, Edit2, Edit3, PlaySquare, AlertCircle, X,
  FileText, Search, Package, Repeat, TrendingUp, TrendingDown, Download, PieChart as PieChartIcon,
  Target, UserCheck, XCircle, Plus, ChevronDown, ChevronUp, Filter, ArrowUpRight, ArrowDownRight, Video, BarChart2
} from 'lucide-react';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  EXCHANGE_RATE, S, iqd2usd, td, pill, btn, today, Dual, StatCard, FilterBtn, Modal, LabeledInput, DetailRow, EmptyRow, DateRangeFilter, VIDEO_TYPES, DeliveryModal, VT_LABEL, VT_FILTER_TABS
} from '../components/SharedUI';


// ═══════════════════════════════════════════════════════════════
// PROJECTS PAGE
// ═══════════════════════════════════════════════════════════════

function printProject(p) {
  const w = window.open('', '_blank');
  const invLine = p.invoice_number
    ? '<div class="row"><span class="label">رقم الفاتورة</span><span>' + p.invoice_number + ' — ' + (p.invoice_payment_status === 'paid' ? '✅ مدفوع' : p.invoice_payment_status === 'partial' ? '🟡 جزئي' : '🔴 غير مدفوع') + '</span></div>'
    : '';
  const notesLine = p.notes
    ? '<div class="row"><span class="label">ملاحظات</span><span>' + p.notes + '</span></div>'
    : '';
  w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"/><title>مشروع — ${p.title}</title><style>body{font-family:Tajawal,sans-serif;padding:32px;color:#0F1E33}h1{font-size:22px;margin-bottom:4px}.meta{color:#64748B;font-size:13px;margin-bottom:24px}.row{display:flex;justify-content:space-between;padding:9px 0;border-bottom:1px solid #EEE;font-size:13px}.label{color:#64748B}@media print{body{padding:0}}</style></head><body><h1>${p.title}</h1><div class="meta">العميل: ${p.client_name} &nbsp;|&nbsp; رقم المشروع: #${p.id}</div><div class="row"><span class="label">نوع الخدمة</span><span>${p.service_type || '—'}</span></div><div class="row"><span class="label">تاريخ البدء</span><span>${p.start_date || '—'}</span></div><div class="row"><span class="label">تاريخ التسليم المتوقع</span><span>${p.due_date || '—'}</span></div><div class="row"><span class="label">تاريخ التسليم الفعلي</span><span>${p.delivered_date || '—'}</span></div><div class="row"><span class="label">الإيرادات</span><span>${formatIQD(p.total_iqd || 0)} / ${formatUSD(p.total_usd || 0)}</span></div><div class="row"><span class="label">التكلفة المباشرة</span><span>${formatIQD(p.direct_cost_iqd || 0)}</span></div><div class="row"><span class="label">صافي الربح</span><span>${formatIQD(p.profit_iqd || 0)} (${p.profit_margin || 0}%)</span></div>${invLine}${notesLine}</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 600);
}

// ─── ProjectDetailModal ───────────────────────────────────────────────────────
const PROJ_STATUS_CFG = {
  draft: { bg: '#F1F5F9', color: 'var(--text-gray, #64748B)', label: 'مسودة' },
  active: { bg: '#DBEAFE', color: '#1E40AF', label: 'نشط' },
  in_progress: { bg: '#DBEAFE', color: '#1E40AF', label: 'جاري' },
  delivered: { bg: '#D1FAE5', color: '#065F46', label: 'مُسلَّم' },
  completed: { bg: '#D1FAE5', color: '#065F46', label: 'مكتمل' },
  closed: { bg: '#F1F5F9', color: 'var(--text-dark-s, #374151)', label: 'مغلق' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B', label: 'ملغي' },
};
const STATUS_NEXT = {
  draft: [{ label: '▶️ بدء المشروع', next: 'active', color: '#17A589' }],
  active: [{ label: '✅ تم التسليم', next: 'delivered', color: '#9B8EC4' },
  { label: '❌ إلغاء', next: 'cancelled', color: '#E74C3C' }],
  in_progress: [{ label: '✅ تم التسليم', next: 'delivered', color: '#9B8EC4' },
  { label: '❌ إلغاء', next: 'cancelled', color: '#E74C3C' }],
  delivered: [{ label: '🔒 إغلاق', next: 'closed', color: 'var(--text-gray, #64748B)' }],
  closed: [],
  completed: [],
  cancelled: [],
};

function ProjectDetailModal({ projectId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [expForm, setExpForm] = useState({ category: 'ads', expense_date: today(), amount_iqd: '', note: '' });
  const [showExpForm, setShowExpForm] = useState(false);
  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm] = useState({ total_iqd: '', notes: '' });

  const load = () => getProjectSummary(projectId).then(setData);
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const handleStatus = async (next) => {
    try {
      await updateProjectStatus(projectId, next);
      toast.success('تم تحديث الحالة');
      load(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
  };

  const handleArchive = async () => {
    if (!window.confirm('أرشفة هذا المشروع؟')) return;
    try { await archiveProject(projectId); toast.success('تم الأرشفة'); onRefresh(); onClose(); }
    catch { toast.error('خطأ في الأرشفة'); }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expForm.amount_iqd) return toast.error('أدخل المبلغ');
    try {
      await createExpense({ ...expForm, amount_usd: parseFloat(expForm.amount_iqd) / 1500, related_project_id: projectId });
      toast.success('تم إضافة المصروف');
      setShowExpForm(false); setExpForm({ category: 'ads', expense_date: today(), amount_iqd: '', note: '' });
      load();
    } catch { toast.error('خطأ في إضافة المصروف'); }
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    if (!invForm.total_iqd) return toast.error('أدخل المبلغ');
    try {
      await createInvoice({
        client_id: data.project.client_id, project_id: projectId, invoice_type: 'project',
        total_iqd: parseFloat(invForm.total_iqd), total_usd: parseFloat(invForm.total_iqd) / 1500,
        notes: invForm.notes || `مشروع: ${data.project.title}`
      });
      toast.success('تم إنشاء الفاتورة');
      setShowInvForm(false); setInvForm({ total_iqd: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
  };

  if (!data) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000 }}>
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', borderRadius: 12, padding: 40, color: 'var(--text-gray, #64748B)' }}>جاري التحميل…</div>
    </div>
  );

  const { project, invoices, expenses, revenue, revenueIssued, totalExpenses, profit, margin } = data;
  const sc = PROJ_STATUS_CFG[project.status] || PROJ_STATUS_CFG.draft;
  const actions = STATUS_NEXT[project.status] || [];
  const expCats = { ads: 'إعلانات', salary: 'موظفين', equipment: 'معدات', misc: 'متنوع', ai_tool: 'اشتراكات', transport: 'مواصلات' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: 16 }}>
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowX: 'auto', display: 'flex', flexDirection: 'column', fontFamily: 'Tajawal, sans-serif', direction: 'rtl' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-light, #E2E8F0)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span className="dark:!text-gray-100" style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)' }}>{project.title}</span>
              <span style={{ ...pill(sc.bg, sc.color) }}>{sc.label}</span>
            </div>
            <div className="dark:!text-gray-400" style={{ fontSize: 13, color: 'var(--text-gray, #64748B)' }}>{project.client_name} · {project.service_type}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {actions.map(a => (
              <button key={a.next} onClick={() => handleStatus(a.next)}
                style={{ ...btn(a.color), padding: '6px 14px', fontSize: 12 }}>
                {a.label}
              </button>
            ))}
            <button onClick={handleArchive} style={{ ...btn('#F1F5F9', 'var(--text-gray, #64748B)'), padding: '6px 12px', fontSize: 12 }}>📦</button>
            <button onClick={onClose} style={{ ...btn('#F1F5F9', 'var(--text-dark-s, #374151)'), padding: '6px 12px' }}><X size={14} /></button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--border-light, #E2E8F0)', padding: '0 24px' }}>
          {[['overview', 'نظرة عامة'], ['invoices', 'الفواتير'], ['expenses', 'المصاريف']].map(([id, label]) => (
            <button key={id} onClick={() => setDetailTab(id)} style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'Tajawal, sans-serif', marginBottom: -2,
              color: detailTab === id ? '#8B5CF6' : 'var(--text-gray, #64748B)',
              borderBottom: `3px solid ${detailTab === id ? '#8B5CF6' : 'transparent'}`
            }}>{label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* ─ Overview ─ */}
          {detailTab === 'overview' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'الإيرادات المحصّلة', val: formatIQD(revenue), color: '#17A589' },
                  { label: 'المصاريف', val: formatIQD(totalExpenses), color: '#E74C3C' },
                  { label: 'صافي الربح', val: formatIQD(profit), color: profit >= 0 ? '#9B8EC4' : '#E74C3C' },
                  { label: 'هامش الربح', val: `${margin}%`, color: margin >= 60 ? '#17A589' : margin >= 30 ? '#E67E22' : '#E74C3C' },
                ].map(({ label, val, color }) => (
                  <div key={label} className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border-light, #E2E8F0)' }}>
                    <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  ['العميل', project.client_name],
                  ['نوع الخدمة', project.service_type],
                  ['النوع', project.type === 'internal' ? 'داخلي' : 'خارجي'],
                  ['تاريخ البدء', project.start_date || '—'],
                  ['تاريخ التسليم', project.due_date || '—'],
                  ['الإيرادات الصادرة', formatIQD(revenueIssued)],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light, #F1F5F9)', fontSize: 13 }}>
                    <span className="dark:!text-gray-400" style={{ color: 'var(--text-gray, #64748B)' }}>{k}</span>
                    <span className="dark:!text-gray-100" style={{ fontWeight: 600, color: 'var(--text-dark-s, #374151)' }}>{v}</span>
                  </div>
                ))}
                {project.notes && (
                  <div style={{ marginTop: 8, padding: 12, background: '#FFFBEB', borderRadius: 8, fontSize: 13, color: '#92400E' }}>
                    📝 {project.notes}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─ Invoices ─ */}
          {detailTab === 'invoices' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div className="dark:!text-gray-100" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark-s, #374151)' }}>{invoices.length} فاتورة</div>
                <button onClick={() => setShowInvForm(v => !v)} style={{ ...btn('#17A589'), padding: '6px 14px', fontSize: 12 }}>
                  + إضافة فاتورة
                </button>
              </div>
              {showInvForm && (
                <form onSubmit={handleAddInvoice} style={{ background: '#F0FDF4', borderRadius: 10, padding: 14, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <div className="dark:!text-gray-100" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark-s, #374151)', marginBottom: 4 }}>المبلغ (د.ع)</div>
                    <input type="text" inputMode="decimal" value={invForm.total_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setInvForm(f => ({ ...f, total_iqd: e.target.value }))} style={{ ...S.input, width: 140 }} required />
                  </div>
                  <div>
                    <div className="dark:!text-gray-100" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark-s, #374151)', marginBottom: 4 }}>ملاحظة</div>
                    <input value={invForm.notes} onChange={e => setInvForm(f => ({ ...f, notes: e.target.value }))} style={{ ...S.input, width: 200 }} placeholder="اختياري" />
                  </div>
                  <button type="submit" style={{ ...btn('#17A589'), padding: '8px 16px' }}>حفظ</button>
                  <button type="button" onClick={() => setShowInvForm(false)} style={{ ...btn('#F1F5F9', 'var(--text-dark-s, #374151)'), padding: '8px 12px' }}>إلغاء</button>
                </form>
              )}
              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                    {['رقم الفاتورة', 'التاريخ', 'الإجمالي', 'المدفوع', 'المتبقي', 'الحالة'].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {invoices.map(inv => {
                      const st = INV_STATUS[inv.payment_status] || INV_STATUS.unpaid;
                      return (
                        <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)' }}>
                          <td style={td({ fontWeight: 600, color: '#9B8EC4' })}>{inv.invoice_number}</td>
                          <td style={td()}>{inv.issue_date}</td>
                          <td style={td()}>{formatIQD(inv.total_iqd)}</td>
                          <td style={td()}>{formatIQD(inv.paid_iqd)}</td>
                          <td style={td({ color: '#E74C3C' })}>{formatIQD((inv.total_iqd || 0) - (inv.paid_iqd || 0))}</td>
                          <td style={td()}><span style={pill(st.bg, st.color)}>{st.label}</span></td>
                        </tr>
                      );
                    })}
                    {invoices.length === 0 && <tr><td colSpan={6} className="dark:!text-gray-400" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #94A3B8)' }}>لا توجد فواتير</td></tr>}
                  </tbody>
                </table>
              </div>
              {invoices.length > 0 && (
                <div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ marginTop: 10, display: 'flex', gap: 24, fontSize: 13, padding: '10px 14px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 8 }}>
                  <span>الإجمالي: <strong>{formatIQD(revenueIssued)}</strong></span>
                  <span style={{ color: '#17A589' }}>المحصّل: <strong>{formatIQD(revenue)}</strong></span>
                  <span style={{ color: '#E74C3C' }}>المتبقي: <strong>{formatIQD(revenueIssued - revenue)}</strong></span>
                </div>
              )}
            </div>
          )}

          {/* ─ Expenses ─ */}
          {detailTab === 'expenses' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div className="dark:!text-gray-100" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark-s, #374151)' }}>{expenses.length} مصروف</div>
                <button onClick={() => setShowExpForm(v => !v)} style={{ ...btn('#E74C3C'), padding: '6px 14px', fontSize: 12 }}>
                  + إضافة مصروف
                </button>
              </div>
              {showExpForm && (
                <form onSubmit={handleAddExpense} style={{ background: '#FEF2F2', borderRadius: 10, padding: 14, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <div className="dark:!text-gray-100" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark-s, #374151)', marginBottom: 4 }}>التصنيف</div>
                    <select value={expForm.category} onChange={e => setExpForm(f => ({ ...f, category: e.target.value }))} style={{ ...S.input, width: 120 }}>
                      {Object.entries(expCats).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="dark:!text-gray-100" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark-s, #374151)', marginBottom: 4 }}>المبلغ (د.ع)</div>
                    <input type="text" inputMode="decimal" value={expForm.amount_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setExpForm(f => ({ ...f, amount_iqd: e.target.value }))} style={{ ...S.input, width: 130 }} required />
                  </div>
                  <div>
                    <div className="dark:!text-gray-100" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark-s, #374151)', marginBottom: 4 }}>التاريخ</div>
                    <input type="date" value={expForm.expense_date} onChange={e => setExpForm(f => ({ ...f, expense_date: e.target.value }))} style={{ ...S.input, width: 140 }} />
                  </div>
                  <div>
                    <div className="dark:!text-gray-100" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dark-s, #374151)', marginBottom: 4 }}>ملاحظة</div>
                    <input value={expForm.note} onChange={e => setExpForm(f => ({ ...f, note: e.target.value }))} style={{ ...S.input, width: 160 }} placeholder="اختياري" />
                  </div>
                  <button type="submit" style={{ ...btn('#E74C3C'), padding: '8px 16px' }}>حفظ</button>
                  <button type="button" onClick={() => setShowExpForm(false)} style={{ ...btn('#F1F5F9', 'var(--text-dark-s, #374151)'), padding: '8px 12px' }}>إلغاء</button>
                </form>
              )}
              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                    {['التاريخ', 'التصنيف', 'الملاحظة', 'المبلغ'].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {expenses.map(exp => (
                      <tr key={exp.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)' }}>
                        <td style={td()}>{exp.expense_date}</td>
                        <td style={td()}><span style={pill('#FEE2E2', '#E74C3C')}>{expCats[exp.category] || exp.category}</span></td>
                        <td style={td({ color: 'var(--text-gray, #64748B)' })}>{exp.note || '—'}</td>
                        <td style={td({ fontWeight: 600, color: '#E74C3C' })}>{formatIQD(exp.amount_iqd)}</td>
                      </tr>
                    ))}
                    {expenses.length === 0 && <tr><td colSpan={4} className="dark:!text-gray-400" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #94A3B8)' }}>لا توجد مصاريف</td></tr>}
                  </tbody>
                </table>
              </div>
              {expenses.length > 0 && (
                <div style={{ marginTop: 10, padding: '10px 14px', background: '#FEF2F2', borderRadius: 8, fontSize: 13 }}>
                  إجمالي المصاريف: <strong style={{ color: '#E74C3C' }}>{formatIQD(totalExpenses)}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectDetail, setProjectDetail] = useState(null); // id of open project detail
  const [menuOpen, setMenuOpen] = useState(null);     // id of row with open 3-dot menu
  const RATE = 1500;
  const [form, setForm] = useState({
    client_id: '', title: '', service_type: 'video',
    start_date: today(), due_date: '', seconds: 0,
    total_usd: '', total_iqd: '',
    direct_cost_usd: '0', direct_cost_iqd: '0',
    notes: '', autoInvoice: true
  });

  const load = useCallback(() => getProjects().then(setProjects), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showCreate) getClients().then(setClients); }, [showCreate]);

  function setSeconds(s) {
    setForm(f => ({ ...f, seconds: s, total_iqd: String(s * RATE), total_usd: s.toFixed(2) }));
  }

  function resetForm() {
    setForm({ client_id: '', title: '', service_type: 'video', start_date: today(), due_date: '', seconds: 0, total_usd: '', total_iqd: '', direct_cost_usd: '0', direct_cost_iqd: '0', notes: '', autoInvoice: true });
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.client_id || !form.title) return toast.error('العميل والعنوان مطلوبان');
    setSaving(true);
    try {
      const data = {
        client_id: Number(form.client_id), title: form.title,
        service_type: form.service_type,
        start_date: form.start_date || null, due_date: form.due_date || null,
        total_usd: parseFloat(form.total_usd) || 0,
        total_iqd: parseFloat(form.total_iqd) || 0,
        direct_cost_usd: parseFloat(form.direct_cost_usd) || 0,
        direct_cost_iqd: parseFloat(form.direct_cost_iqd) || 0,
        notes: form.notes || null,
      };
      const res = await createProject(data);
      if (form.autoInvoice && res.id) {
        await createInvoice({
          client_id: data.client_id, project_id: res.id,
          invoice_type: 'project', total_usd: data.total_usd, total_iqd: data.total_iqd,
          notes: 'مشروع: ' + form.title,
        });
        toast.success('تم إنشاء المشروع والفاتورة');
      } else {
        toast.success('تم إنشاء المشروع');
      }
      setShowCreate(false); resetForm(); load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل إنشاء المشروع');
    } finally { setSaving(false); }
  }

  async function completeProject(p) {
    try {
      await updateProject(p.id, {
        title: p.title, service_type: p.service_type,
        start_date: p.start_date, due_date: p.due_date,
        delivered_date: today(), status: 'completed',
        total_usd: p.total_usd, total_iqd: p.total_iqd,
        direct_cost_usd: p.direct_cost_usd, direct_cost_iqd: p.direct_cost_iqd,
        notes: p.notes
      });
      toast.success('تم تسليم المشروع ✅');
      load();
    } catch { toast.error('فشل تحديث المشروع'); }
  }

  const filtered = projects.filter(p => {
    if (tab !== 'all' && p.status !== tab) return false;
    if (search && !p.title.includes(search) && !p.client_name.includes(search)) return false;
    return true;
  });

  const totalRevIqd = projects.reduce((s, p) => s + (p.total_iqd || 0), 0);
  const inProgressCount = projects.filter(p => p.status === 'in_progress').length;
  const completedCount = projects.filter(p => p.status === 'completed').length;

  const grouped = {};
  filtered.forEach(p => {
    const key = p.client_name || 'غير محدد';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(p);
  });

  const statusCfg = {
    draft: { bg: '#F1F5F9', color: 'var(--text-gray, #64748B)', label: 'مسودة' },
    in_progress: { bg: '#DBEAFE', color: '#1E40AF', label: 'جاري' },
    completed: { bg: '#D1FAE5', color: '#065F46', label: 'مكتمل' },
    cancelled: { bg: '#FEE2E2', color: '#991B1B', label: 'ملغي' },
  };
  const invColor = { unpaid: '#E74C3C', partial: '#E67E22', paid: '#17A589' };
  const invLabel = { unpaid: 'غير مدفوع', partial: 'جزئي', paid: 'مدفوع' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="dark:!text-gray-100" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart2 size={20} color="#9B8EC4" /> المشاريع
          <span className="dark:!text-gray-400" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted, #94A3B8)', marginRight: 4 }}>({filtered.length})</span>
        </h1>
        <button onClick={() => setShowCreate(true)} style={btn()}>
          <Plus size={15} /> مشروع جديد
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        <StatCard label="إجمالي المشاريع" rawValue={projects.length} color="#9B8EC4" icon="📁" />
        <StatCard label="جارية" rawValue={inProgressCount} color="#1E40AF" icon="⏳" />
        <StatCard label="مكتملة" rawValue={completedCount} color="#065F46" icon="✅" />
        <StatCard label="إجمالي الإيرادات" iqd={totalRevIqd} color="#E67E22" icon="💰" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {[['all', 'الكل'], ['in_progress', 'جاري'], ['completed', 'مكتمل'], ['draft', 'مسودة'], ['cancelled', 'ملغي']].map(([v, l]) => (
          <FilterBtn key={v} active={tab === v} onClick={() => setTab(v)}>{l}</FilterBtn>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="بحث باسم المشروع أو العميل..."
          style={{ ...S.input, width: 220, marginRight: 'auto' }} />
      </div>

      {/* Grouped by client */}
      {Object.keys(grouped).length === 0 ? (
        <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-400 dark:!shadow-none" style={{ ...S.cardPad, textAlign: 'center', color: 'var(--text-muted, #94A3B8)', padding: 48 }}>لا توجد مشاريع</div>
      ) : Object.entries(grouped).map(([clientName, projs]) => {
        const isCollapsed = collapsed.has(clientName);
        const clientRevIqd = projs.reduce((s, p) => s + (p.total_iqd || 0), 0);
        return (
          <div key={clientName} style={{ ...S.card, marginBottom: 14 }}>
            <div
              onClick={() => setCollapsed(prev => { const n = new Set(prev); isCollapsed ? n.delete(clientName) : n.add(clientName); return n; })}
              style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: 'var(--bg-flat-50, #F8FAFC)', borderBottom: isCollapsed ? 'none' : '1px solid var(--border-light, #E2E8F0)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Users size={15} color="#9B8EC4" />
                <span className="dark:!text-gray-100" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-dark-h, #0F1E33)' }}>{clientName}</span>
                <span style={pill('#F0ECF7', '#1E40AF')}>{projs.length} مشروع</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#E67E22' }}>{formatIQD(clientRevIqd)}</span>
                <span className="dark:!text-gray-400" style={{ color: 'var(--text-muted, #94A3B8)', fontSize: 13 }}>{isCollapsed ? '▼' : '▲'}</span>
              </div>
            </div>
            {!isCollapsed && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #FAFAFA)' }}>
                    <th style={S.th}>المشروع</th>
                    <th style={S.th}>الحالة</th>
                    <th style={S.th}>تاريخ التسليم</th>
                    <th style={S.th}>الفاتورة</th>
                    <th style={S.th}>الإيرادات</th>
                    <th style={S.th}>الربح</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {projs.map(p => {
                    const sc = statusCfg[p.status] || statusCfg.draft;
                    const overdue = p.due_date && p.due_date < today() && p.status === 'in_progress';
                    const isMenuOpen = menuOpen === p.id;
                    return (
                      <tr key={p.id}
                        onClick={() => setProjectDetail(p.id)}
                        style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', cursor: 'pointer', transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={td()}>
                          <div className="dark:!text-gray-100" style={{ fontWeight: 600, color: 'var(--text-dark-h, #0F1E33)' }}>{p.title}</div>
                          {p.service_type && <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 1 }}>{p.service_type}</div>}
                        </td>
                        <td style={td()}><span style={pill(sc.bg, sc.color)}>{sc.label}</span></td>
                        <td style={td()}>
                          <span style={{ fontSize: 13, color: overdue ? '#E74C3C' : 'var(--text-dark-s, #374151)', fontWeight: overdue ? 700 : 400 }}>
                            {p.due_date || '—'}{overdue && ' ⚠️'}
                          </span>
                        </td>
                        <td style={td()}>
                          {p.invoice_number ? (
                            <div>
                              <div className="dark:!text-gray-100" style={{ fontSize: 12, color: 'var(--text-dark-s, #374151)' }}>{p.invoice_number}</div>
                              <span style={{ ...pill(p.invoice_payment_status === 'paid' ? '#D1FAE5' : p.invoice_payment_status === 'partial' ? '#FEF3C7' : '#FEE2E2', invColor[p.invoice_payment_status] || 'var(--text-gray, #64748B)'), marginTop: 2, display: 'inline-block' }}>
                                {invLabel[p.invoice_payment_status] || '—'}
                              </span>
                            </div>
                          ) : <span style={{ color: '#CBD5E1', fontSize: 12 }}>—</span>}
                        </td>
                        <td style={td()}><Dual iqd={p.total_iqd} usd={p.total_usd} /></td>
                        <td style={td()}>
                          <div style={{ fontWeight: 600, color: (p.profit_iqd || 0) >= 0 ? '#17A589' : '#E74C3C' }}>{formatIQD(p.profit_iqd || 0)}</div>
                          <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>{p.profit_margin || 0}%</div>
                        </td>
                        <td style={{ ...td(), whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => setMenuOpen(isMenuOpen ? null : p.id)}
                              style={{ ...btn('#F1F5F9', 'var(--text-gray, #64748B)'), padding: '5px 10px', fontSize: 16, lineHeight: 1 }}
                            >⋮</button>
                            {isMenuOpen && (
                              <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ position: 'absolute', left: 0, top: '100%', background: 'var(--bg-flat-white, white)', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,.15)', border: '1px solid var(--border-light, #E2E8F0)', zIndex: 999, minWidth: 160, overflowX: 'auto' }}>
                                {[
                                  { icon: '🔍', label: 'تفاصيل', action: () => { setProjectDetail(p.id); setMenuOpen(null); } },
                                  { icon: '🖨️', label: 'طباعة الملخص', action: () => { printProject(p); setMenuOpen(null); } },
                                  { icon: '✅', label: 'تم التسليم', action: async () => { await completeProject(p); setMenuOpen(null); }, show: p.status === 'in_progress' },
                                  { icon: '📦', label: 'أرشفة', action: async () => { await archiveProject(p.id); load(); setMenuOpen(null); } },
                                  {
                                    icon: '🗑️', label: 'حذف نهائي', danger: true, show: isOwnerOrAdmin, action: async () => {
                                      if (!window.confirm(`حذف المشروع "${p.title}" نهائياً؟`)) return;
                                      try { await deleteProject(p.id); toast.success('تم حذف المشروع'); load(); setMenuOpen(null); }
                                      catch (err) { toast.error(err.response?.data?.error || 'خطأ في الحذف'); }
                                    }
                                  },
                                ].filter(m => m.show !== false).map(m => (
                                  <button key={m.label} onClick={m.action}
                                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'Tajawal, sans-serif', textAlign: 'right', color: m.danger ? '#DC2626' : 'var(--text-dark-s, #374151)' }}
                                  >{m.icon} {m.label}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {/* Create modal */}
      {showCreate && (
        <Modal onClose={() => { setShowCreate(false); resetForm(); }} title="مشروع جديد" width={540}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <LabeledInput label="العميل" required>
              <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} required style={S.input}>
                <option value="">— اختر العميل —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </LabeledInput>
            <LabeledInput label="عنوان المشروع" required>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required style={S.input} placeholder="مثال: مقطع ترويجي — إبريل 2025" />
            </LabeledInput>
            <LabeledInput label="نوع الخدمة">
              <select value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} style={S.input}>
                <option value="video">فيديو</option>
                <option value="audio">صوت</option>
                <option value="design">تصميم</option>
                <option value="other">أخرى</option>
              </select>
            </LabeledInput>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <LabeledInput label="تاريخ البدء">
                <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={S.input} />
              </LabeledInput>
              <LabeledInput label="تاريخ التسليم">
                <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={S.input} />
              </LabeledInput>
            </div>
            <LabeledInput label="المدة بالثواني" hint="اختر مدة سريعة أو أدخل يدوياً — تُحسب الميزانية تلقائياً">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {[15, 30, 45, 60, 90, 120].map(s => (
                  <button key={s} type="button" onClick={() => setSeconds(s)}
                    style={{ ...btn(form.seconds === s ? '#9B8EC4' : '#F1F5F9', form.seconds === s ? 'white' : 'var(--text-dark-s, #374151)'), padding: '5px 12px', fontSize: 12 }}>
                    {s}s
                  </button>
                ))}
              </div>
              <input type="text" inputMode="decimal" min="0" value={form.seconds || ''} placeholder="أو أدخل الثواني يدوياً"
                onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                onChange={e => setSeconds(Number(e.target.value))} style={S.input} />
            </LabeledInput>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <LabeledInput label="الميزانية (دينار)">
                <input type="text" inputMode="decimal" min="0" value={form.total_iqd}
                  onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                  onChange={e => setForm(f => ({ ...f, total_iqd: e.target.value, total_usd: (parseFloat(e.target.value || 0) / RATE).toFixed(2) }))}
                  style={S.input} />
              </LabeledInput>
              <LabeledInput label="الميزانية (دولار)">
                <input type="text" inputMode="decimal" min="0" step="0.01" value={form.total_usd}
                  onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                  onChange={e => setForm(f => ({ ...f, total_usd: e.target.value, total_iqd: String(Math.round(parseFloat(e.target.value || 0) * RATE)) }))}
                  style={S.input} />
              </LabeledInput>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <LabeledInput label="التكلفة المباشرة (دينار)">
                <input type="text" inputMode="decimal" min="0" value={form.direct_cost_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, direct_cost_iqd: e.target.value }))} style={S.input} />
              </LabeledInput>
              <LabeledInput label="التكلفة المباشرة (دولار)">
                <input type="text" inputMode="decimal" min="0" step="0.01" value={form.direct_cost_usd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, direct_cost_usd: e.target.value }))} style={S.input} />
              </LabeledInput>
            </div>
            <LabeledInput label="ملاحظات">
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ ...S.input, minHeight: 70, resize: 'vertical' }} />
            </LabeledInput>
            <label className="dark:!text-gray-100" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-dark-s, #374151)' }}>
              <input type="checkbox" checked={form.autoInvoice} onChange={e => setForm(f => ({ ...f, autoInvoice: e.target.checked }))} />
              إنشاء فاتورة تلقائياً
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
              <button type="button" onClick={() => { setShowCreate(false); resetForm(); }} style={btn('#F1F5F9', 'var(--text-dark-s, #374151)')}>إلغاء</button>
              <button type="submit" disabled={saving} style={btn()}>{saving ? 'جاري الحفظ...' : 'إنشاء المشروع'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Project Detail Modal */}
      {projectDetail && (
        <ProjectDetailModal
          projectId={projectDetail}
          onClose={() => setProjectDetail(null)}
          onRefresh={load}
        />
      )}

      {/* Close menu on outside click */}
      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setMenuOpen(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════
// ─── Role config ──────────────────────────────────────────────────────────────
const ROLES = {
  owner: { label: 'مالك', color: '#DC2626', bg: '#FEE2E2' },
  admin: { label: 'مدير', color: '#7C3AED', bg: '#EDE9FE' },
  manager: { label: 'مشرف', color: '#9B8EC4', bg: '#F0ECF7' },
  staff: { label: 'موظف', color: '#17A589', bg: '#D1FAE5' },
  viewer: { label: 'مشاهد فقط', color: 'var(--text-gray, #64748B)', bg: '#F1F5F9' },
};

const DEFAULT_PERMS = {
  owner: { clients: true, invoices: true, expenses: true, subscriptions: true, reports: true, settings: true, delete: true },
  admin: { clients: true, invoices: true, expenses: true, subscriptions: true, reports: true, settings: true, delete: true },
  manager: { clients: true, invoices: true, expenses: true, subscriptions: true, reports: true, settings: false, delete: false },
  staff: { clients: true, invoices: true, expenses: false, subscriptions: true, reports: false, settings: false, delete: false },
  viewer: { clients: false, invoices: false, expenses: false, subscriptions: false, reports: true, settings: false, delete: false },
};

const PERM_LABELS = [
  { key: 'clients', label: 'إدارة العملاء' },
  { key: 'invoices', label: 'الفواتير والمدفوعات' },
  { key: 'expenses', label: 'المصاريف' },
  { key: 'subscriptions', label: 'الاشتراكات' },
  { key: 'reports', label: 'التقارير' },
  { key: 'settings', label: 'الإعدادات' },
  { key: 'delete', label: 'الحذف' },
];

const BLANK_USER_FORM = {
  username: '', password: '', confirmPassword: '',
  full_name: '', role: 'staff',
  permissions: { ...DEFAULT_PERMS.staff }
};

function UserFormModal({ title, initial, onSave, onClose, isEdit }) {
  const [form, setForm] = useState(initial || BLANK_USER_FORM);
  const [saving, setSaving] = useState(false);

  const setRole = (role) => setForm(f => ({ ...f, role, permissions: { ...DEFAULT_PERMS[role] } }));
  const togglePerm = (key) => setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && form.password !== form.confirmPassword)
      return toast.error('كلمتا السر غير متطابقتين');
    if (!isEdit && form.password.length < 6)
      return toast.error('كلمة السر يجب أن تكون 6 أحرف على الأقل');
    if (isEdit && form.password && form.password !== form.confirmPassword)
      return toast.error('كلمتا السر غير متطابقتين');
    setSaving(true);
    try {
      const payload = {
        username: form.username, full_name: form.full_name,
        role: form.role, permissions: form.permissions,
      };
      if (form.password) payload.password = form.password;
      if (!isEdit) payload.username = form.username;
      await onSave(payload);
    } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} title={title} width={500}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!isEdit && (
          <LabeledInput label="اسم المستخدم" required>
            <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/\s/g, '') }))}
              required style={S.input} placeholder="بدون مسافات" />
          </LabeledInput>
        )}
        <LabeledInput label="الاسم الكامل">
          <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
            style={S.input} placeholder="اختياري" />
        </LabeledInput>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <LabeledInput label={isEdit ? 'كلمة السر الجديدة (اختياري)' : 'كلمة السر'} required={!isEdit}>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required={!isEdit} style={S.input} placeholder={isEdit ? 'اتركها فارغة للإبقاء' : ''} />
          </LabeledInput>
          <LabeledInput label="تأكيد كلمة السر" required={!isEdit}>
            <input type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
              required={!isEdit} style={S.input} />
          </LabeledInput>
        </div>
        <LabeledInput label="الدور">
          <select value={form.role} onChange={e => setRole(e.target.value)} style={S.input}>
            {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </LabeledInput>
        <div>
          <div className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-gray, #64748B)', fontWeight: 600, marginBottom: 8 }}>الصلاحيات</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {PERM_LABELS.map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.permissions[key]} onChange={() => togglePerm(key)} />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <button type="button" onClick={onClose} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-light, #E2E8F0)', background: 'var(--bg-flat-white, white)', cursor: 'pointer', fontSize: 13, fontFamily: 'Tajawal, sans-serif' }}>إلغاء</button>
          <button type="submit" disabled={saving} style={{ ...btn(), opacity: saving ? .6 : 1 }}>
            {saving ? 'جاري الحفظ...' : (isEdit ? 'حفظ التغييرات' : 'إضافة المستخدم')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

